import React, { memo, MouseEventHandler, useState, useMemo } from 'react'
import classNames from 'classnames'
import get from 'lodash/get'
import DownArrowIcon from 'transfix-icons/build/DownArrowIcon'
import Badge, { BADGE_COLOR } from 'transfix-ui/components/Badge'
import { INavigationRoute } from 'transfix-ui/components/NavigationBar/types'
import Tooltip from 'transfix-ui/components/Tooltip'
import { createStyles, makeStyles } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { brandColors, TBrandColor } from 'transfix-ui/theme/colors'
import { LockedIcon } from 'transfix-icons'
import SubRoutesMenu from './SubRoutesMenu'

const getIconColor = ({
  isOpen,
  variant,
}: {
  variant: 'default' | 'error'
  isOpen: boolean
}): TBrandColor => {
  if (isOpen) {
    return get(
      {
        default: 'coolGray6' as const,
        error: 'error1' as const,
      },
      variant
    )
  }

  return get(
    {
      default: 'coolGray8' as const,
      error: 'error1' as const,
    },
    variant
  )
}

const getToggleIconColor = (isOpen: boolean): TBrandColor => {
  return isOpen ? 'coolGray6' : 'coolGray8'
}

interface IUseNavigationMobileItemLinkStylesProps {
  /**
   * @default false
   */
  isOpen?: boolean
  /**
   * @example 'default'
   */
  variant: 'error' | 'default'
}

type TClassKey =
  | 'root'
  | 'link'
  | 'text'
  | 'arrow'
  | 'dividerBefore'
  | 'iconWrapper'
  | 'textWrapper'
  | 'arrowWrapper'
  | 'lockedIconWrapper'
  | 'notificationWrapper'
  | 'linkDefault'
  | 'linkError'
  | 'linkOpen'
  | 'textLocked'
  | 'linkActive'

const useNavigationMobileItemLinkStyles = makeStyles(theme => {
  return createStyles<TClassKey, IUseNavigationMobileItemLinkStylesProps>({
    root: {
      display: 'flex',
      flex: '0 0 100%',
      width: '100%',
      flexDirection: 'row',
      position: 'relative',
      flexWrap: 'wrap',
      backgroundColor: brandColors.white,
      color: brandColors.black,
      alignItems: 'center',
      padding: 0,
    },
    dividerBefore: {
      borderTop: `1px solid ${brandColors.coolGray3}`,
    },
    link: {
      flex: '0 0 100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2),

      '&:hover, &:active': {
        textDecoration: 'none',
      },

      '&:focus': {
        textDecoration: 'none',
      },
    },
    linkDefault: {
      color: brandColors.coolGray8,

      '&:hover, &:active': {
        textDecoration: 'none',
        color: brandColors.coolGray8,
        backgroundColor: brandColors.coolGray1,
      },

      '&:focus': {
        textDecoration: 'none',
        backgroundColor: brandColors.coolGray1,
        color: brandColors.coolGray8,
      },
    },
    linkError: {
      color: brandColors.error1,

      '&:hover, &:active': {
        textDecoration: 'none',
        color: brandColors.error1,
        backgroundColor: brandColors.error0,
      },

      '&:focus': {
        textDecoration: 'none',
        backgroundColor: brandColors.error0,
        color: brandColors.error1,
      },
    },
    linkOpen: {
      color: brandColors.coolGray8,
      backgroundColor: brandColors.coolGray2,

      '&:hover, &:active': {
        textDecoration: 'none',
        color: brandColors.coolGray8,
        backgroundColor: brandColors.coolGray2,
      },

      '&:focus': {
        textDecoration: 'none',
        backgroundColor: brandColors.coolGray2,
        color: brandColors.coolGray8,
      },
    },
    linkActive: {
      color: brandColors.skyBlue6,
      backgroundColor: brandColors.skyBlue1,

      '&:hover, &:active': {
        textDecoration: 'none',
        color: brandColors.skyBlue7,
        backgroundColor: brandColors.skyBlue2,
      },

      '&:focus': {
        textDecoration: 'none',
        backgroundColor: brandColors.skyBlue2,
        color: brandColors.skyBlue7,
      },
    },
    iconWrapper: {
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      paddingRight: theme.spacing(1.5),
    },
    textWrapper: {
      flex: '1 1 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    text: {
      fontWeight: 600,
    },
    textLocked: {
      color: brandColors.coolGray5,
    },
    arrowWrapper: {
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
    },
    arrow: {
      transform: ({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)'),
    },
    lockedIconWrapper: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    notificationWrapper: {
      paddingLeft: theme.spacing(1),
    },
  })
})

interface INavigationMobileItemLinkProps {
  route: INavigationRoute
  onClose: () => void
}

const NavigationMobileItemLink = memo<INavigationMobileItemLinkProps>(({ onClose, route }) => {
  const {
    onClick,
    childRoutes = [],
    dividerBefore = true,
    Icon = () => null,
    location,
    name,
    notificationCount,
    isLocked,
    isActive,
    variant = 'default',
  } = route || {}
  const [isOpen, setOpen] = useState<boolean>(false)
  const classes = useNavigationMobileItemLinkStyles({ isOpen, variant })

  const toggleOpen = () => setOpen(prev => !prev)

  const clickHandler: MouseEventHandler<HTMLAnchorElement> = e => {
    if (childRoutes.length) {
      e.preventDefault()
      toggleOpen()
    } else if (onClick) {
      e.preventDefault()
      onClick()
      onClose()
    } else {
      onClose()
    }
  }

  const className = classNames(classes.root, { [classes.dividerBefore]: dividerBefore })

  const badgeLabel = useMemo(() => [notificationCount, 'new'].join(' '), [notificationCount])
  const iconColor = useMemo(() => getIconColor({ variant, isOpen }), [variant, isOpen])
  const toggleIconColor = useMemo(() => getToggleIconColor(isOpen), [isOpen])
  const hasNotifications = typeof notificationCount === 'number' && notificationCount > 0

  const linkClassName = classNames(classes.link, {
    [classes.linkDefault]: variant === 'default',
    [classes.linkError]: variant === 'error',
    [classes.linkOpen]: isOpen,
    /**
     * @note Only show active style if there are no child routes
     */
    [classes.linkActive]: isActive && !childRoutes?.length,
  })

  const textClassName = classNames(classes.text, {
    [classes.textLocked]: isLocked,
  })

  return (
    <li data-test='NavigationMobileItemLink' className={className}>
      <a href={location} className={linkClassName} onClick={clickHandler}>
        <div className={classes.iconWrapper}>
          <Icon size='large' color={iconColor} />
        </div>
        <div className={classes.textWrapper}>
          <Typography variant='body2' className={textClassName} component='div'>
            {name}
          </Typography>
          {hasNotifications && (
            <div className={classes.notificationWrapper}>
              <Badge label={badgeLabel} color={BADGE_COLOR.blue} />
            </div>
          )}
          {isLocked && (
            <div className={classes.lockedIconWrapper}>
              <Tooltip
                effect='solid'
                id='locked-info'
                position='bottom'
                trigger={<LockedIcon color='coolGray5' size='medium' />}
                type='dark'>
                Admin permission required to view this page
              </Tooltip>
            </div>
          )}
        </div>
        <div className={classes.arrowWrapper}>
          {childRoutes.length > 0 && (
            <DownArrowIcon size='large' color={toggleIconColor} className={classes.arrow} />
          )}
        </div>
      </a>
      {!!childRoutes?.length && (
        <SubRoutesMenu routes={childRoutes} isOpen={isOpen} onClose={onClose} />
      )}
    </li>
  )
})

export default NavigationMobileItemLink
